import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  Button,
  Drawer,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { ReactComponent as EditIcon } from "assets/edit.svg";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getMerchantConfig, getRmsConfig } from "features/config/configSlice";
import BankForm from "../BankForm";
import { Controller, useForm } from "react-hook-form";
import { ReactComponent as CloseIcon } from "assets/close.svg";
import {
  addRefundJSON,
  addRefundSource,
  getRefundSource,
} from "features/returnedProducts/returnedProductSlice";
import parse from "html-react-parser";
import { getSelectedOrder } from "features/orders/orderSlice";
import { submitDetailsApi, verifyBankDetailsApi } from "utils/api";
import { toast } from "react-toastify";
import {
  StyledPaymentDiv,
  StyledPaymentMode,
} from "components/v1/ReturnCart/styled";
// import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import { ReactComponent as UPIIcon } from "assets/upi.svg";
import { ReactComponent as BankIcon } from "assets/bank.svg";
import { ReactComponent as CouponIcon } from "assets/coupon.svg";
import { ReactComponent as WalletIcon } from "assets/wallet.svg";
import {
  getStoreCreditSource,
} from "utils/refundConfig";
import { getApiErrorMessage } from "utils/apiError";
import {
  IykaRefundBreakdown,
  isIykaPolicyQuote,
} from "components/common/IykaRefundBreakdown";

const BANK_VERIFICATION_FALLBACK = "Something is wrong. Please try again.";
const SUBMIT_DETAILS_FALLBACK = "Error! Please try again in some time.";

export const PriceBox = ({
  infoType,
  setOpenBankForm,
  refundSource,
  returnItems,
  accHolderName,
  accountNumber,
  confirmAccNumber,
  ifsc,
  upiValue,
  setReturnItems,
  returnType,
  paymentDetails,
  calculatingLoader,
  returnedProducts,
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const product_applicable_refund_modes =
    returnedProducts &&
    returnedProducts.length > 0 &&
    returnedProducts
      .filter((a) => a.return_type === "refund")
      .map((a) => a.applicable_refund_modes)
      .flat();

  // Selectors
  const rmsConfig = useSelector(getRmsConfig);
  const selectedOrder = useSelector(getSelectedOrder);
  const merchantConfig = useSelector(getMerchantConfig);

  // States
  const [submitDetails, setSubmitDetails] = useState(false);
  const [detailsSubmitted, setDetailsSubmitted] = useState(false);
  const [detailsMethod, setDetailsMethod] = useState(null);
  const [submittingDetails, setSubmitttingDetails] = useState(false);
  const [verificationError, setVerificationError] = useState("");
  // const [specialRefundMode, setSpecialRefundMode] = useState(null);
  const [specialRefundModeLabel, setSpecialRefundModeLabel] = useState(null);
  const selectedRefundSource = useSelector(getRefundSource);
  const orderApplicableRefundModes = Array.isArray(
    selectedOrder?.applicable_refund_modes,
  )
    ? selectedOrder.applicable_refund_modes.filter(Boolean)
    : selectedOrder?.applicable_refund_modes
      ? [selectedOrder.applicable_refund_modes]
      : [];
  const refundModeRestrictedProducts =
    returnedProducts?.filter((item) => item.return_type === "refund") || [];
  const productApplicableRefundModes = Array.isArray(
    product_applicable_refund_modes,
  )
    ? product_applicable_refund_modes.filter(Boolean)
    : [];
  const hasOrderRefundModeRestrictions = Array.isArray(
    selectedOrder?.applicable_refund_modes,
  );
  const hasProductRefundModeRestrictions = refundModeRestrictedProducts.some((item) =>
    Array.isArray(item?.applicable_refund_modes),
  );
  const includesAnyRefundMode = (availableModes, aliases) =>
    aliases.some((alias) => availableModes.includes(alias));
  const orderAllowsRefundMode = (...aliases) =>
    !hasOrderRefundModeRestrictions ||
    includesAnyRefundMode(orderApplicableRefundModes, aliases);
  const productAllowsRefundMode = (...aliases) =>
    !hasProductRefundModeRestrictions ||
    includesAnyRefundMode(productApplicableRefundModes, aliases);
  const allowsSourceRefundMode =
    orderAllowsRefundMode("refund_to_source", "source_refund") &&
    productAllowsRefundMode("refund_to_source", "source_refund");
  const allowsGiftCardRefundMode =
    orderAllowsRefundMode("gift_card") && productAllowsRefundMode("gift_card");
  const allowsCouponRefundMode =
    orderAllowsRefundMode("coupon_code") &&
    productAllowsRefundMode("coupon_code");
  const allowsStoreCreditRefundMode =
    orderAllowsRefundMode("store_credit") &&
    productAllowsRefundMode("store_credit");
  const allowsShopifyStoreCreditRefundMode =
    orderAllowsRefundMode("shopify_store_credit") &&
    productAllowsRefundMode("shopify_store_credit");
  const resolvedStoreCreditSource = getStoreCreditSource(rmsConfig);
  const bankTransferMode = rmsConfig?.refund_config?.modes?.bank_transfer;
  const upiMode = rmsConfig?.refund_config?.modes?.upi;
  const bankTransferAvailable = rmsConfig?.refund_config?.modes
    ? !!bankTransferMode?.enabled && !!bankTransferMode?.available
    : (selectedOrder.is_cod
        ? !!rmsConfig.collect_bank_details_for_cod_orders
        : !!rmsConfig.customer_bank_enabled);
  const upiAvailable = rmsConfig?.refund_config?.modes
    ? !!upiMode?.enabled && !!upiMode?.available
    : (selectedOrder.is_cod
        ? !!rmsConfig.collect_upi_details_for_cod_orders
        : !!rmsConfig.customer_upi_enabled);
  const allowsBankOrUpiRefundMode =
    (hasProductRefundModeRestrictions ||
      !hasOrderRefundModeRestrictions ||
      orderAllowsRefundMode("bank_transfer", "payment_link")) &&
    productAllowsRefundMode("bank_transfer", "payment_link");

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();

  const handleSubmitUPIDetails = async ({ upi }) => {
    setSubmitttingDetails(true);
    setVerificationError("");
    const shouldVerify = rmsConfig.bank_details_verification_enabled;
    const phone = selectedOrder?.user_phone_number || null;

    try {
      if (shouldVerify) {
        await verifyBankDetailsApi({
          ifsc: null,
          account_number: null,
          name: null,
          phone: phone ? Number(phone) : null,
          vpa: upi,
        });
      }

      await submitDetailsApi(
        merchantConfig.merchant_id,
        returnItems.id,
        "upi",
        "",
        0,
        0,
        "",
        upi
      );
      setDetailsSubmitted(true);
      setReturnItems({
        ...returnItems,
        refund_json: {
          upi: {
            upi_code: upi,
          },
        },
      });
      toast.success("Details have been successfully submitted", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      setSubmitDetails(false);
    } catch (error) {
      const errorMessage = getApiErrorMessage(
        error,
        shouldVerify ? BANK_VERIFICATION_FALLBACK : SUBMIT_DETAILS_FALLBACK
      );
      setVerificationError(errorMessage);
      toast.error(
        errorMessage,
        {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
        }
      );
    } finally {
      setSubmitttingDetails(false);
    }
  };

  const handleSubmitBankDetails = async ({
    accountnum,
    name,
    confirmAccountnum,
    ifsc,
  }) => {
    setSubmitttingDetails(true);
    setVerificationError("");
    const shouldVerify = rmsConfig.bank_details_verification_enabled;
    const phone = selectedOrder?.user_phone_number || null;

    try {
      if (shouldVerify) {
        await verifyBankDetailsApi({
          ifsc,
          account_number: Number(accountnum),
          name,
          phone: phone ? Number(phone) : null,
          vpa: null,
        });
      }

      await submitDetailsApi(
        merchantConfig.merchant_id,
        returnItems.id,
        "bank_transfer",
        name,
        Number(accountnum),
        Number(confirmAccountnum),
        ifsc,
        ""
      );
      setDetailsSubmitted(true);
      setReturnItems({
        ...returnItems,
        refund_json: {
          bank_details: {
            bank_account_holder_name: name,
            bank_account_number: accountnum,
          },
        },
      });
      toast.success("Details have been successfully submitted", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      setSubmitDetails(false);
    } catch (error) {
      const errorMessage = getApiErrorMessage(
        error,
        shouldVerify ? BANK_VERIFICATION_FALLBACK : SUBMIT_DETAILS_FALLBACK
      );
      setVerificationError(errorMessage);
      toast.error(
        errorMessage,
        {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
        }
      );
    } finally {
      setSubmitttingDetails(false);
    }
  };

  const shouldPopulateTransferDetails =
    hasProductRefundModeRestrictions ||
    !hasOrderRefundModeRestrictions ||
    (orderApplicableRefundModes.includes("payment_link") &&
      (!hasProductRefundModeRestrictions ||
        productApplicableRefundModes.includes("payment_link")));

  function updateRefundSource(source, shouldHandleTransferDetails = false) {
    setOpenBankForm(false);
    dispatch(addRefundSource(source));

    if (
      !shouldHandleTransferDetails ||
      (source !== "bank_transfer" && source !== "upi")
    ) {
      return;
    }

    if (
      !rmsConfig.use_direct_transfer_for_refund &&
      shouldPopulateTransferDetails
    ) {
      if (source === "upi") {
        dispatch(
          addRefundJSON({
            upi: {
              upi_code: upiValue,
            },
            refund_method: "upi",
          }),
        );
      } else {
        dispatch(
          addRefundJSON({
            bank_details: {
              bank_account_number: accountNumber,
              confirm_bank_account_number: confirmAccNumber,
              bank_account_holder_name: accHolderName,
              bank_ifsc_code: ifsc,
            },
            refund_method: "bank_transfer",
          }),
        );
      }
      return;
    }

    setOpenBankForm(true);
  }

  const BonusChargesSection = paymentDetails?.applicable_offer_amount > 0 && (
    <p
      style={{
        background: "#16AC37",
        color: "#fff",
        padding: "0px 4px",
        fontSize: "10px",
        width: "fit-content",
        borderTopRightRadius: "4px",
        borderBottomLeftRadius: "4px",
      }}
    >
      + ₹ {paymentDetails?.applicable_offer_amount}{" "}
    </p>
  );

  // Utility function to get charges map
  const getSourceChargesMap = (all_mode_charges) => {
    if (!all_mode_charges || Object.keys(all_mode_charges).length === 0) {
      return {};
    }

    if (all_mode_charges.all_modes) {
      return {
        bank_transfer: all_mode_charges.all_modes || 0,
        upi: all_mode_charges.all_modes || 0,
        refund_to_source: all_mode_charges.all_modes || 0,
        coupon: all_mode_charges.all_modes || 0,
        gift_card: all_mode_charges.all_modes || 0,
        store_credit: all_mode_charges.all_modes || 0,
        shopify_store_credit: all_mode_charges.all_modes || 0,
      };
    }

    return {
      bank_transfer: all_mode_charges.bank_transfer || 0,
      upi: all_mode_charges.bank_transfer || 0,
      refund_to_source: all_mode_charges.source_refund || 0,
      coupon: all_mode_charges.coupon || 0,
      gift_card: all_mode_charges.gift_card || 0,
      store_credit: all_mode_charges.store_credit || 0,
      shopify_store_credit: all_mode_charges.shopify_store_credit || 0,
    };
  };

  // ChargesSection component
  const ChargesSection = ({ source }) => {
    const { all_mode_charges } = paymentDetails || {};
    const sourceMap = getSourceChargesMap(all_mode_charges);

    if (sourceMap[source] > 0) {
      return <p className="handling-charges">- ₹ {sourceMap[source]}</p>;
    }

    return null; // Explicitly return null when there's no content
  };

  // getCharges function
  const getCharges = (source, paymentDetails) => {
    const { all_mode_charges } = paymentDetails || {};
    const sourceMap = getSourceChargesMap(all_mode_charges);
    return sourceMap[source] || 0;
  };

  const renderIcon = (sourceValue) => {
    const icons = {
      gift_card: <CardGiftcardIcon style={{ fontSize: "20px" }} />,
      upi: <UPIIcon style={{ fontSize: "14px" }} />,
      bank_transfer: <BankIcon style={{ fontSize: "14px" }} />,
      refund_to_source: <BankIcon style={{ fontSize: "14px" }} />,
      coupon: <CouponIcon style={{ fontSize: "14px" }} />,
      default: <WalletIcon style={{ fontSize: "14px" }} />,
    };
    return icons[sourceValue] || icons.default;
  };

  const visibleRefundModeOptions = useMemo(() => {
    if (location.pathname !== "/return-cart") {
      return [];
    }

    const options = [];

    if (bankTransferAvailable && allowsBankOrUpiRefundMode) {
      options.push({
        label: "Bank Account",
        value: "bank_transfer",
      });
    }
    if (upiAvailable && allowsBankOrUpiRefundMode) {
      options.push({
        label: "UPI",
        value: "upi",
      });
    }
    if (rmsConfig.coupon_refund_enabled && allowsCouponRefundMode) {
      options.push({
        label: rmsConfig.coupon_method_name,
        value: "coupon",
      });
    }
    if (
      rmsConfig.refund_to_source &&
      allowsSourceRefundMode
    ) {
      options.push({
        label: "Source Account",
        value: "refund_to_source",
      });
    }
    if (
      rmsConfig.store_credit &&
      resolvedStoreCreditSource &&
      allowsStoreCreditRefundMode
    ) {
      options.push({
        label: rmsConfig.store_credit_method_name,
        value: resolvedStoreCreditSource,
      });
    }
    if (
      rmsConfig.shopify_store_credit &&
      allowsShopifyStoreCreditRefundMode
    ) {
      options.push({
        label: "Shopify Store Credits",
        value: "shopify_store_credit",
      });
    }
    if (rmsConfig.gift_card && allowsGiftCardRefundMode) {
      options.push({
        label: rmsConfig.gift_card_method_name,
        value: "gift_card",
      });
    }

    if (options.length === 0 && allowsBankOrUpiRefundMode) {
      options.push({
        label: "Bank Account",
        value: "bank_transfer",
      });
    }

    return options;
  }, [
    allowsBankOrUpiRefundMode,
    allowsCouponRefundMode,
    allowsGiftCardRefundMode,
    allowsShopifyStoreCreditRefundMode,
    allowsSourceRefundMode,
    allowsStoreCreditRefundMode,
    bankTransferAvailable,
    location.pathname,
    rmsConfig.coupon_refund_enabled,
    rmsConfig.gift_card,
    rmsConfig.gift_card_method_name,
    rmsConfig.refund_to_source,
    rmsConfig.shopify_store_credit,
    rmsConfig.store_credit,
    rmsConfig.store_credit_method_name,
    rmsConfig.coupon_method_name,
    resolvedStoreCreditSource,
    upiAvailable,
  ]);

  useEffect(() => {
    if (location.pathname !== "/return-cart") {
      return;
    }

    setSpecialRefundModeLabel(rmsConfig.offer_refund_mode_label);

    if (
      visibleRefundModeOptions.length === 0 ||
      visibleRefundModeOptions.some(
        (option) => option.value === selectedRefundSource,
      )
    ) {
      return;
    }

    const nextRefundSource = visibleRefundModeOptions[0].value;
    setOpenBankForm(false);
    dispatch(addRefundSource(nextRefundSource));

    if (
      visibleRefundModeOptions.length !== 1 ||
      (nextRefundSource !== "bank_transfer" && nextRefundSource !== "upi")
    ) {
      return;
    }

    if (
      !rmsConfig.use_direct_transfer_for_refund &&
      shouldPopulateTransferDetails
    ) {
      if (nextRefundSource === "upi") {
        dispatch(
          addRefundJSON({
            upi: {
              upi_code: upiValue,
            },
            refund_method: "upi",
          }),
        );
      } else {
        dispatch(
          addRefundJSON({
            bank_details: {
              bank_account_number: accountNumber,
              confirm_bank_account_number: confirmAccNumber,
              bank_account_holder_name: accHolderName,
              bank_ifsc_code: ifsc,
            },
            refund_method: "bank_transfer",
          }),
        );
      }
      return;
    }

    setOpenBankForm(true);
  }, [
    accountNumber,
    accHolderName,
    confirmAccNumber,
    dispatch,
    ifsc,
    location.pathname,
    rmsConfig.offer_refund_mode_label,
    rmsConfig.use_direct_transfer_for_refund,
    selectedRefundSource,
    setOpenBankForm,
    shouldPopulateTransferDetails,
    upiValue,
    visibleRefundModeOptions,
  ]);

  const RadioLabel = ({ sourceValue, sourceLabel }) => {
    return (
      // <FormControlLabel
      //   id={sourceValue}
      //   value={sourceValue}
      //   control={
      //     <Radio
      //       color="default"
      //       defaultChecked={refundOptions === 1}
      //       size="small"
      //       checked={refundSource === sourceValue}
      //       style={
      //         paymentDetails?.offer_refund_mode === sourceValue
      //           ? { color: "#16AC37" }
      //           : {}
      //       }
      //       onClick={()=>{
      //         updateRefundSource(sourceValue);
      //         if (sourceValue === "bank_transfer" || sourceValue === "upi") {
      //           if (
      //             !rmsConfig.use_direct_transfer_for_refund &&
      //             (product_applicable_refund_modes.length > 0 ||
      //               selectedOrder.applicable_refund_modes.length === 0 ||
      //               (selectedOrder.applicable_refund_modes.includes(
      //                 "payment_link"
      //               ) &&
      //                 (product_applicable_refund_modes.length === 0 ||
      //                   product_applicable_refund_modes.includes(
      //                     "payment_link"
      //                   ))))
      //           ) {
      //             if (sourceValue === "upi") {
      //               dispatch(
      //                 addRefundJSON({
      //                   upi: {
      //                     upi_code: upiValue,
      //                   },
      //                   refund_method: "upi",
      //                 })
      //               );
      //             } else {
      //               dispatch(
      //                 addRefundJSON({
      //                   bank_details: {
      //                     bank_account_number: accountNumber,
      //                     confirm_bank_account_number: confirmAccNumber,
      //                     bank_account_holder_name: accHolderName,
      //                     bank_ifsc_code: ifsc,
      //                   },
      //                   refund_method: "bank_transfer",
      //                 })
      //               );
      //             }
      //           } else {
      //             setOpenBankForm(true);
      //           }
      //         }
      //       }}
      //       onChange={() => {
      //         updateRefundSource(sourceValue);
      //         if (sourceValue === "bank_transfer" || sourceValue === "upi") {
      //           if (
      //             !rmsConfig.use_direct_transfer_for_refund &&
      //             (product_applicable_refund_modes.length > 0 ||
      //               selectedOrder.applicable_refund_modes.length === 0 ||
      //               (selectedOrder.applicable_refund_modes.includes(
      //                 "payment_link"
      //               ) &&
      //                 (product_applicable_refund_modes.length === 0 ||
      //                   product_applicable_refund_modes.includes(
      //                     "payment_link"
      //                   ))))
      //           ) {
      //             if (sourceValue === "upi") {
      //               dispatch(
      //                 addRefundJSON({
      //                   upi: {
      //                     upi_code: upiValue,
      //                   },
      //                   refund_method: "upi",
      //                 })
      //               );
      //             } else {
      //               dispatch(
      //                 addRefundJSON({
      //                   bank_details: {
      //                     bank_account_number: accountNumber,
      //                     confirm_bank_account_number: confirmAccNumber,
      //                     bank_account_holder_name: accHolderName,
      //                     bank_ifsc_code: ifsc,
      //                   },
      //                   refund_method: "bank_transfer",
      //                 })
      //               );
      //             }
      //           } else {
      //             setOpenBankForm(true);
      //           }
      //         }
      //       }}
      //     />
      //   }
      //   label={
      //     <p
      //       className="radio-label"
      //       style={{
      //         display: "flex",
      //         alignItems: "center",
      //       }}
      //     >
      //       {sourceLabel}
      //       {paymentDetails?.offer_refund_mode === sourceValue &&
      //         BonusChargesSection}
      //       <ChargesSection source={sourceValue} />
      //     </p>
      //   }
      // />
      <StyledPaymentMode
        primary_color={merchantConfig.primary_theme_color}
        onClick={() => {
          updateRefundSource(sourceValue, true);
        }}
        selected={selectedRefundSource === sourceValue}
        style={{
          borderRadius: merchantConfig.use_rounded_edge_buttons ? "4px" : "0px",
          position: "relative",
        }}
        bonus={paymentDetails?.offer_refund_mode === sourceValue}
        charges={getCharges(sourceValue, paymentDetails)}
      >
        {renderIcon(sourceValue)}
        <span style={{ marginTop: "5px" }}>{sourceLabel}</span>
        {/* {selectedRefundSource === sourceValue && (
          <CheckBoxIcon
            style={{ fill: "#fff", position: "absolute", left: "0", top: "0" }}
          />
        )} */}
        <span
          style={{
            position: "absolute",
            right: "-1px",
            top: "-1px",
          }}
        >
          {paymentDetails?.offer_refund_mode === sourceValue &&
            BonusChargesSection}
          {getCharges(sourceValue, paymentDetails) > 0 && (
            <ChargesSection source={sourceValue} />
          )}
        </span>
      </StyledPaymentMode>
    );
  };

  return (
    <div className="box" id={`${infoType}mode`}>
      {/* Excahnge Box  */}

      {infoType == "Exchange" ? (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {returnItems &&
            returnItems !== null &&
            returnItems.exchange_details.length > 0 && (
              <div style={{ display: "block", marginTop: "5px" }}>
                <p className="title"> Exchange Details </p>
                {returnItems.exchange_details.map((detail) => {
                  return (
                    <div style={{ marginTop: "10px" }}>
                      <p style={{ textTransform: "capitalize" }}>
                        Status: {detail.exchange_status.replace("_", " ")}
                      </p>
                      <p>
                        {" "}
                        Exchange order number :{" "}
                        {detail.exchange_order_number}{" "}
                      </p>
                      <Button
                        style={{
                          margin: "10px 0px",
                          textTransform: "capitalize",
                        }}
                        variant="outlined"
                      >
                        <a
                          style={{ color: "inherit", textDecoration: "none" }}
                          target="_blank"
                          rel="noreferrer"
                          href={detail.exchange_order_url}
                        >
                          View Order
                        </a>
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      ) : (
        (returnType == "refund" || returnType == "refund_exchange") &&
        infoType == "Return" && (
          <div>
            {location.pathname == "/return-cart" && (
              <div>
                <p className="title" style={{ marginBottom: "6px" }}>
                  Choose a Refund Mode{" "}
                </p>
                <FormControl
                  style={{
                    width: "100%",
                    display: "flex",
                  }}
                >
                  <RadioGroup
                    aria-labelledby="refund-options"
                    name="refund-options"
                    row
                    style={{ flexWrap: "wrap", justifyContent: "center" }}
                  >
                    {visibleRefundModeOptions.map((option) => (
                      <RadioLabel
                        key={option.value}
                        sourceLabel={option.label}
                        sourceValue={option.value}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
                <hr style={{ margin: "1rem 0 0" }} />
              </div>
            )}
            {returnItems &&
            returnItems !== null &&
            returnItems.refund_details &&
            returnItems.refund_details !== null &&
            returnItems.refund_details.length > 0 ? (
              returnItems.refund_details.map((detail) => {
                return (
                  <div>
                    <p
                      style={{
                        margin: "10px 0px",
                        color: "#3B444D",
                        textTransform: "capitalize",
                      }}
                    >
                      {" "}
                      Refund preference selected&nbsp; - &nbsp;
                      {detail.refund_method === "bank_transfer" ? (
                        "Bank transfer"
                      ) : detail.refund_method === "upi" ? (
                        "UPI"
                      ) : detail.refund_method === "refund_to_source" ? (
                        <span style={{ textTransform: "initial" }}>
                          Your refund has been processed to your original mode
                          of payment
                        </span>
                      ) : detail.refund_method == "coupon" ? (
                        <span style={{ fontWeight: "600" }}>
                          {rmsConfig.coupon_method_name}
                        </span>
                      ) : detail.refund_method !== null ? (
                        detail.refund_method.replace("_", " ")
                      ) : (
                        detail.customer_preferred_refund_method ||
                        returnItems.customer_preferred_refund_method
                      )}
                    </p>
                    {detail.refund_url !== null && (
                      <p>
                        Collect your amount{" "}
                        <a
                          href={detail.refund_url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: "inherit" }}
                        >
                          here
                        </a>
                      </p>
                    )}
                    {(detail.refund_method === "bank_transfer" ||
                      detail.refund_method === "bank_transfer_razorpayx" ||
                      detail.refund_method === "bank_transfer_cashfree") &&
                    rmsConfig.use_direct_transfer_for_refund ? (
                      <div>
                        {detail.account_holder_name &&
                          detail.account_holder_name !== null && (
                            <p>
                              {detail.account_holder_name} &nbsp; - &nbsp;{" "}
                              {detail.account_number.replace(
                                /^(\d+)(\d{4})/,
                                "*******$2",
                              )}
                            </p>
                          )}
                        {detail.vpa !== null && (
                          <p>
                            UPI ID:{" "}
                            {detail.vpa.replace(/^(\d+)(\d{4})/, "*******$2")}
                          </p>
                        )}
                        <p>
                          Refund Status -{" "}
                          {detail.refund_status !== null &&
                            detail.refund_status.replace("_", " ")}
                        </p>
                      </div>
                    ) : (
                      location.pathname === "/tracking" &&
                      (detail.refund_method == "bank_transfer" ||
                        detail.refund_method == "upi") &&
                      !detailsSubmitted && (
                        <div>
                          <p
                            style={{
                              color: "rgb(51, 51, 51)",
                              fontSize: "16px",
                              marginBottom: "10px",
                            }}
                          >
                            Please select the mode of refund
                          </p>
                          <FormControl>
                            <RadioGroup
                              row
                              aria-labelledby="refund-options"
                              name="refund-options"
                            >
                              {rmsConfig.customer_bank_enabled && (
                                <FormControlLabel
                                  id="bank"
                                  value="transfer"
                                  control={
                                    <Radio
                                      color="default"
                                      size="small"
                                      onChange={() => {
                                        setSubmitDetails(true);
                                        setDetailsMethod("Bank");
                                        setVerificationError("");
                                      }}
                                    />
                                  }
                                  label={
                                    <p className="radio-label">Bank Account</p>
                                  }
                                />
                              )}
                              {rmsConfig.customer_upi_enabled && (
                                <FormControlLabel
                                  id="upi"
                                  value="upi"
                                  control={
                                    <Radio
                                      color="default"
                                      size="small"
                                      onChange={() => {
                                        setSubmitDetails(true);
                                        setDetailsMethod("UPI");
                                        setVerificationError("");
                                      }}
                                    />
                                  }
                                  label={<p className="radio-label">UPI</p>}
                                />
                              )}
                            </RadioGroup>
                          </FormControl>

                          <Drawer
                            classes={{ paper: "paper-drawer" }}
                            anchor="bottom"
                            open={submitDetails}
                            onClose={() => {
                              setSubmitDetails(false);
                              setVerificationError("");
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.08)",
                                  height: "80px",
                                  padding: "32px",
                                }}
                              >
                                <p
                                  style={{
                                    fontSize: "1rem",
                                    fontWeight: "500",
                                    color: "#3B444D",
                                  }}
                                >
                                  Enter {detailsMethod} Details
                                </p>
                                <p
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    setSubmitDetails(false);
                                    setVerificationError("");
                                  }}
                                >
                                  {" "}
                                  <CloseIcon
                                    style={{
                                      width: "24px",
                                      height: "24px",
                                      stroke: "#3B444D",
                                    }}
                                  />{" "}
                                </p>
                              </div>
                              {detailsMethod == "Bank" ? (
                                <BankForm
                                  handleSubmitBankDetails={
                                    handleSubmitBankDetails
                                  }
                                  submittingDetails={submittingDetails}
                                  errorMessage={verificationError}
                                />
                              ) : (
                                <form
                                  onSubmit={handleSubmit(
                                    handleSubmitUPIDetails,
                                  )}
                                  style={{ padding: "20px" }}
                                >
                                  <Controller
                                    control={control}
                                    defaultValue=""
                                    rules={{
                                      required: true,
                                      validate: (value) =>
                                        !/@paytm$/i.test(value) ||
                                        "Paytm UPI IDs are not supported",
                                      pattern: {
                                        value:
                                          /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/,
                                        message: "Invalid UPI ID",
                                      },
                                    }}
                                    name="upi"
                                    render={({ field }) => (
                                      <TextField
                                        variant="outlined"
                                        fullWidth
                                        id="upi"
                                        label="UPI ID"
                                        error={Boolean(errors.upi)}
                                        helperText={
                                          errors.upi
                                            ? errors.upi.type === "validate"
                                              ? `Paytm UPI IDs are not supported`
                                              : errors.upi.type === "pattern"
                                                ? "Invalid UPI ID"
                                                : "UPI code is required"
                                            : ""
                                        }
                                        inputProps={{ type: "text" }}
                                        style={{ marginTop: "20px" }}
                                        {...field}
                                      />
                                    )}
                                  />
                                  {verificationError && (
                                    <p
                                      style={{
                                        color: "#D32F2F",
                                        marginTop: "12px",
                                      }}
                                    >
                                      {verificationError}
                                    </p>
                                  )}
                                  <Button
                                    type="submit"
                                    color="primary"
                                    variant="contained"
                                    disabled={submittingDetails}
                                    style={{
                                      padding: "1rem",
                                      borderRadius:
                                        !merchantConfig.use_rounded_edge_buttons
                                          ? "0px"
                                          : "4px",
                                      margin: "40px auto",
                                      display: "block",
                                      cursor: "pointer",
                                      textTransform: "capitalize",
                                    }}
                                  >
                                    {submittingDetails
                                      ? "Submitting..."
                                      : "Submit"}
                                  </Button>
                                </form>
                              )}
                            </div>
                          </Drawer>
                        </div>
                      )
                    )}
                    {detail.refund_method === "upi" && detail.vpa !== "" && (
                      <p>
                        UPI ID:{" "}
                        {detail.vpa.replace(/^(\d+)(\d{4})/, "*******$2")}
                      </p>
                    )}
                    {detail.refund_method === "coupon" && (
                      <p>
                        {detail.refund_id !== null && (
                          <span>Coupon ID: {detail.refund_id} and</span>
                        )}
                        {detail.refund_amount !== null && (
                          <span>
                            {" "}
                            Coupon discount:{" "}
                            <span style={{ fontWeight: "600" }}>
                              ₹ {detail.refund_amount}
                            </span>
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                );
              })
            ) : (
              <div>
                {location.pathname !== "/return-cart" && (
                  <p
                    className="refund-preference-title"
                    style={{
                      fontWeight: "600",
                      margin: "1rem 0px",
                    }}
                  >
                    Refund Details
                  </p>
                )}
                {/* {((returnItems && returnItems !== null) ||
                  (refundSource &&
                    refundSource !== null &&
                    refundSource !== "")) && (
                  <p
                    style={{
                      margin: "0px 0px 10px",
                      color: "#3B444D",
                      textTransform: "capitalize",
                    }}
                    className="refund-preference"
                  >
                    {" "}
                    Refund preference selected&nbsp; - &nbsp;
                    <span style={{ fontWeight: "600" }}>
                      {refundSource === "bank_transfer" ? (
                        "Bank transfer"
                      ) : refundSource === "upi" ? (
                        "UPI"
                      ) : refundSource === "refund_to_source" ? (
                        <span style={{ textTransform: "initial" }}>
                          Your refund will be processed to your original mode of
                          payment
                        </span>
                      ) : refundSource == "coupon" ? (
                        rmsConfig.coupon_method_name
                      ) : refundSource !== null ? (
                        refundSource.replace("_", " ")
                      ) : (
                        refundSource ||
                        returnItems.customer_preferred_refund_method
                      )}
                    </span>
                  </p>
                )} */}
              </div>
            )}

            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginTop: "1rem",
              }}
            >
              {accHolderName &&
              accHolderName !== "" &&
              accountNumber &&
              accountNumber !== "" &&
              refundSource === "bank_transfer" ? (
                <p
                  style={{
                    margin: "10px 0px",
                    color: "#3B444D",
                    fontSize: "16px",
                  }}
                >
                  {" "}
                  {accHolderName} &nbsp; - &nbsp;{" "}
                  {accountNumber.replace(/^(\d+)(\d{4})/, "*******$2")}
                </p>
              ) : (
                refundSource === "upi" &&
                upiValue &&
                upiValue !== "" && (
                  <p style={{ fontSize: "16px" }}>UPI ID - {upiValue}</p>
                )
              )}

              {refundSource != null &&
                (refundSource == "bank_transfer" || refundSource == "upi") &&
                rmsConfig.use_direct_transfer_for_refund &&
                location.pathname == "/return-cart" && (
                  <EditIcon
                    style={{ stroke: "#1d8dcc", marginLeft: "6px" }}
                    onClick={() => setOpenBankForm(true)}
                  />
                )}
            </div>

            {paymentDetails && paymentDetails !== null && (
              <StyledPaymentDiv style={{ padding: "10px 0px 0px" }}>
                <p className="payment-title">Refund Details</p>
                <div className="subtotal-statement">
                  <p>
                    Subtotal of Products (Refund)
                  </p>
                  <p
                    style={{
                      color: "3B444D",
                      fontWeight: "500",
                      whiteSpace: "nowrap",
                      marginLeft: "1rem",
                    }}
                  >
                    ₹ {paymentDetails.refund_product_total}
                  </p>
                </div>

              {!isIykaPolicyQuote(paymentDetails) && paymentDetails.refund_charges +
                paymentDetails.preapplied_refund_charge >
                  0 && (
                  <div className="subtotal-statement">
                    <p>
                      {paymentDetails.refund_charge_label &&
                      paymentDetails.refund_charge_label !== ""
                        ? paymentDetails.refund_charge_label
                        : "Handling Charges"}
                    </p>
                    <p
                      style={{
                        color: "#e74c3c",
                        fontWeight: "500",
                        marginLeft: "1rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      ₹{" "}
                      {paymentDetails.refund_charges +
                        paymentDetails.preapplied_refund_charge}
                    </p>
                  </div>
                )}

              {!isIykaPolicyQuote(paymentDetails) && paymentDetails.shipping_charges > 0 && (
                  <div className="subtotal-statement">
                    <p>
                      {paymentDetails.shipping_charge_label &&
                      paymentDetails.shipping_charge_label !== ""
                        ? paymentDetails.shipping_charge_label
                        : "Shipping Charges"}
                    </p>
                    <p
                      style={{
                        color: "#e74c3c",
                        fontWeight: "500",
                        marginLeft: "1rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      ₹ {paymentDetails.shipping_charges}
                    </p>
                  </div>
                )}

              <IykaRefundBreakdown paymentDetails={paymentDetails} />

              {!isIykaPolicyQuote(paymentDetails) && (paymentDetails.refund_mode_offer_amount > 0 ||
                  paymentDetails.item_level_refund_mode_offer_amount > 0) &&
                  paymentDetails.offer_refund_mode === refundSource && (
                    <div className="subtotal-statement">
                      <p>Bonus</p>
                      <p
                        style={{
                          color: "#16AC37",
                          fontWeight: "500",
                          marginLeft: "1rem",
                          whiteSpace: "nowrap",
                        }}
                      >
                        + ₹{" "}
                        {paymentDetails.offer_refund_mode_on === "item_level"
                          ? paymentDetails.total_refund_mode_offer_amount
                          : paymentDetails.refund_mode_offer_amount}
                      </p>
                    </div>
                  )}

                {paymentDetails.refund_amount >= 0 && (
                  <div
                    style={{
                      borderTop: "1px dashed #3B444DB2",
                      borderBottom: "1px dashed #3B444DB2",
                      padding: "0.5rem 0px",
                      margin: "1rem 0",
                      fontWeight: 700
                    }}
                    className="subtotal-statement"
                  >
                    <Fragment>
                      <p>
                        Total Amount to be refunded
                      </p>
                      <p
                        style={{
                          color: "#16AC37",
                          marginLeft: "1rem",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {calculatingLoader
                          ? "Calculating..."
                          : `₹ ${paymentDetails.refund_amount}`}
                      </p>
                    </Fragment>
                  </div>
                )}

                {refundSource !== paymentDetails.offer_refund_mode &&
                  paymentDetails.applicable_offer_amount > 0 && (
                    <p
                      style={{
                        background: "#fffbeb",
                        border: "1px solid #fee686",
                        color: "#963d1f",
                        padding: "5px 10px",
                        fontSize: "14px",
                        borderRadius: merchantConfig.use_rounded_edge_buttons
                          ? "10xp"
                          : "0px",
                        width: "fit-content",
                        marginBottom: "1rem",
                      }}
                    >
                      Skip the Handling charges and get a{" "}
                      <span style={{ fontWeight: "600" }}>
                        {" "}
                        ₹ {paymentDetails?.applicable_offer_amount} bonus{" "}
                      </span>{" "}
                      by choosing{" "}
                      <span style={{ fontWeight: "600" }}>
                        {specialRefundModeLabel &&
                        specialRefundModeLabel !== "" &&
                        paymentDetails?.offer_refund_mode !== null
                          ? specialRefundModeLabel
                          : paymentDetails?.offer_refund_mode !== null &&
                            paymentDetails?.offer_refund_mode.replace("_", " ")}
                      </span>{" "}
                    </p>
                  )}
              </StyledPaymentDiv>
            )}

            {rmsConfig.brand_refund_note &&
              rmsConfig.brand_refund_note !== null &&
              rmsConfig.brand_refund_note !== "" &&
              returnType !== "exchange" && (
                <div
                  style={{
                    margin: "10px 0px",
                    color: "rgba(51, 51, 51, 0.7)",
                    listStyle: "inside",
                  }}
                  className="refund-note"
                >
                  <p
                    style={{
                      color: "#DD435D99",
                      marginRight: "5px",
                      fontWeight: "600",
                      textDecoration: "underline",
                      textUnderlineOffset: "5px",
                      marginBottom: "10px",
                      fontSize: "14px",
                    }}
                  >
                    Refund Note:
                  </p>
                  {parse(rmsConfig.brand_refund_note)}
                </div>
              )}

            {rmsConfig.brand_exchange_note &&
              rmsConfig.brand_exchange_note !== null &&
              rmsConfig.brand_exchange_note !== "" &&
              returnType !== "refund" && (
                <div
                  style={{
                    margin: "10px 0px",
                    color: "rgba(51, 51, 51, 0.7)",
                    listStyle: "inside",
                  }}
                  className="refund-note"
                >
                  <p
                    style={{
                      color: "#DD435D99",
                      marginRight: "5px",
                      fontWeight: "600",
                      textDecoration: "underline",
                      textUnderlineOffset: "5px",
                      marginBottom: "10px",
                      fontSize: "14px",
                    }}
                  >
                    {rmsConfig.only_replacement ? "Replacement" : "Exchange"}{" "}
                    Note:
                  </p>
                  {parse(rmsConfig.brand_exchange_note)}
                </div>
              )}
          </div>
        )
      )}
    </div>
  );
};
